/**
 * Beregninger for oppfølginger (CRM-2.2). Ren kalkylefil, ingen Supabase/React.
 * Alle dato- og ukegrenser regnes i Europe/Oslo, uavhengig av maskinens tidssone.
 */
import { addDaysToDateString } from "./missing-hours";
import { osloDateParts, osloDateString } from "./oslo-time";
import type { DBFollowUp, FollowUpPriority } from "./db-types";
import type { StatusTone } from "./widget-status";

/** Utfallene på en oppfølging i køen. */
export type FollowUpAction = "email" | "phone" | "postpone" | "close";

export const PRIORITY_LABELS: Record<FollowUpPriority, string> = {
  asap: "ASAP",
  this_week: "Denne uken",
  nice: "Nice to do",
};

const PRIORITY_RANK: Record<FollowUpPriority, number> = { asap: 0, this_week: 1, nice: 2 };

/** ASAP → miss, denne uken → near, nice → nøytral (null = ingen KPI-farge). */
export function priorityTone(priority: FollowUpPriority): StatusTone | null {
  if (priority === "asap") return "miss";
  if (priority === "this_week") return "near";
  return null;
}

const OSLO_WALL = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Oslo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/** Oslo-lokal dato og klokkeslett til riktig UTC-øyeblikk (tåler sommer-/vintertid). */
function osloWallToInstant(dateStr: string, hour: number, minute: number, second: number): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d, hour, minute, second);
  let guess = wall - 2 * 3600_000;
  for (let i = 0; i < 2; i++) {
    const map: Record<string, number> = {};
    for (const p of OSLO_WALL.formatToParts(new Date(guess))) {
      if (p.type !== "literal") map[p.type] = Number(p.value);
    }
    const shown = Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute, map.second);
    guess += wall - shown;
  }
  return new Date(guess);
}

export interface OsloWeekBounds {
  /** Mandag 00:00:00 Oslo. */
  start: Date;
  /** Søndag 23:59:59 Oslo. */
  end: Date;
  /** Mandag som YYYY-MM-DD. */
  startDate: string;
  /** Søndag som YYYY-MM-DD. */
  endDate: string;
}

/** Uka (mandag til søndag) som inneholder `now`, i Oslo-tid. */
export function osloWeekBounds(now: Date): OsloWeekBounds {
  const today = osloDateString(now);
  const { year, month, day } = osloDateParts(now);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = søndag
  const sinceMonday = (weekday + 6) % 7;
  const startDate = addDaysToDateString(today, -sinceMonday);
  const endDate = addDaysToDateString(startDate, 6);
  return {
    start: osloWallToInstant(startDate, 0, 0, 0),
    end: osloWallToInstant(endDate, 23, 59, 59),
    startDate,
    endDate,
  };
}

type Sortable = Pick<DBFollowUp, "priority" | "due_date" | "created_at">;

/**
 * Køens rekkefølge: ASAP, så denne uken, så nice. Innenfor prioritet kommer
 * forfalte først (de har tidligst dato), så stigende forfall, så eldste først.
 */
export function sortQueue<T extends Sortable>(followUps: readonly T[]): T[] {
  return [...followUps].sort((a, b) => {
    const byPriority = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (byPriority !== 0) return byPriority;
    if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
    if (a.created_at !== b.created_at) return a.created_at < b.created_at ? -1 : 1;
    return 0;
  });
}

export interface WeekProgress {
  done: number;
  total: number;
  /** Åpne oppfølginger som teller denne uka. */
  remaining: number;
  /** Åpne oppfølginger med forfall før i dag. */
  overdue: number;
}

/**
 * «Din uke». Totalen er åpne oppfølginger med forfall senest søndag (forfalte
 * medregnet) pluss de som er fullført i uka. Fullført = status done; en utsatt
 * oppfølging er fortsatt åpen og teller aldri som gjort. Utsettes den forbi
 * søndag, forlater den uka.
 */
export function weekProgress(
  followUps: readonly Pick<DBFollowUp, "status" | "due_date" | "closed_at">[],
  now: Date,
): WeekProgress {
  const { start, endDate } = osloWeekBounds(now);
  const today = osloDateString(now);
  let done = 0;
  let remaining = 0;
  let overdue = 0;
  for (const f of followUps) {
    if (f.status === "open" && f.due_date <= endDate) {
      remaining += 1;
      if (f.due_date < today) overdue += 1;
    } else if (f.status === "done" && f.closed_at && new Date(f.closed_at) >= start) {
      done += 1;
    }
  }
  return { done, total: done + remaining, remaining, overdue };
}

export interface DueQuickPick {
  key: "tomorrow" | "three_days" | "next_week";
  label: string;
  date: string;
}

/** Hurtigvalg i «Neste steg»-dialogen: i morgen, 3 dager, neste uke (mandag). */
export function dueQuickPicks(now: Date): DueQuickPick[] {
  const { startDate } = osloWeekBounds(now);
  const today = osloDateString(now);
  return [
    { key: "tomorrow", label: "I morgen", date: addDaysToDateString(today, 1) },
    { key: "three_days", label: "3 dager", date: addDaysToDateString(today, 3) },
    { key: "next_week", label: "Neste uke", date: addDaysToDateString(startDate, 7) },
  ];
}

export interface SplitQueue<T> {
  /** Åpne, i køens rekkefølge. */
  open: T[];
  /** Fullførte i uka, nyeste først (vises gjennomstreket nederst i listen). */
  done: T[];
  /** Kortet som vises: det valgte hvis det fortsatt er åpent, ellers først i køen. */
  current: T | null;
}

/** Deler oppfølgingene i åpne (sortert), fullførte og kortet som skal vises. */
export function splitQueue<T extends Sortable & Pick<DBFollowUp, "id" | "status" | "closed_at">>(
  followUps: readonly T[],
  focusId?: string | null,
): SplitQueue<T> {
  const open = sortQueue(followUps.filter((f) => f.status === "open"));
  const done = followUps
    .filter((f) => f.status === "done")
    .sort((a, b) => (b.closed_at ?? "").localeCompare(a.closed_at ?? ""));
  const current = open.find((f) => f.id === focusId) ?? open[0] ?? null;
  return { open, done, current };
}

/** Godkjenningskortet for et møtereferat (CRM-4.6): primærknappen åpner referatet, ikke et mailutkast. */
export function isSummaryApproval(followUp: { rule_key: string | null; source: string }): boolean {
  return followUp.source === "meeting_summary" && followUp.rule_key === "summary_approval";
}

/** «Skriv notater fra møtet» (CRM-4.5): fysisk møte uten transkripsjon. */
export function isMeetingNotes(followUp: { rule_key: string | null }): boolean {
  return followUp.rule_key === "meeting_notes";
}

/** Oppfølginger som hører til referatsiden i stedet for mailutkast, og knappeteksten for å åpne den. */
export function meetingFollowUpLabel(followUp: { rule_key: string | null; source: string }): string | null {
  if (isSummaryApproval(followUp)) return "Åpne referatet";
  if (isMeetingNotes(followUp)) return "Skriv notater";
  return null;
}

/** Primærhandling i køen (pil høyre og Enter): telefon når kanalen er telefon, ellers e-post. */
export function primaryActionFor(followUp: Pick<DBFollowUp, "channel">): FollowUpAction {
  return followUp.channel === "phone" ? "phone" : "email";
}
